import type { RailBranch } from "../../data/rail-branches";
import { MS_PER_PIXEL } from "../timeline/book/BookLayout";
import { branchOffsetY } from "../timeline/rail/RailBranchMotion/motion";
import { timelineHomeMarkerCenterX } from "../timeline/scene/SceneMotion/motion";
import type { TimelineVertex } from "./vertex-primitives";
import { pushWorldLine, type WorldView } from "./world-to-canvas";

export const SCENE_MARK_HALF_LEN = 22;
const MARK_HALF_WIDTH = 6.5;

export interface SceneStart {
  startMs: number;
  locationLabel: string;
}

export interface SceneChangeMark {
  x: number;
  y: number;
}

/** Where each scene start sits on the rail that hosts it, for what is labeled there. */
export function sceneChangeMarks(
  sceneStarts: readonly SceneStart[],
  branches: readonly RailBranch[],
  axisY: number,
  viewportWidth: number,
): SceneChangeMark[] {
  return marksAt(sceneStarts, viewportWidth, (start) => sceneMarkYs(branches, axisY, start));
}

/** The ticks to draw: every rail running at a scene's start, so split rails keep time together. */
export function railTickMarks(
  sceneStarts: readonly SceneStart[],
  branches: readonly RailBranch[],
  axisY: number,
  viewportWidth: number,
): SceneChangeMark[] {
  return marksAt(sceneStarts, viewportWidth, (start) => (
    branches.length === 0
      ? [axisY]
      : branches
        .filter((item) => start.startMs >= item.startMs && start.startMs < item.endMs)
        .map((item) => axisY + branchOffsetY(item, start.startMs))
  ));
}

function marksAt(
  sceneStarts: readonly SceneStart[],
  viewportWidth: number,
  railYs: (start: SceneStart) => readonly number[],
): SceneChangeMark[] {
  const marks: SceneChangeMark[] = [];
  const seen = new Set<string>();
  for (const start of sceneStarts) {
    const x = railWorldX(start.startMs, viewportWidth);
    for (const y of railYs(start)) {
      const key = `${x}:${y}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      marks.push({ x, y });
    }
  }
  return marks;
}

export function appendSceneChangeMarks(
  verts: TimelineVertex[],
  sceneStarts: readonly SceneStart[],
  branches: readonly RailBranch[],
  axisY: number,
  viewportWidth: number,
  view: WorldView,
): void {
  for (const mark of railTickMarks(
    sceneStarts, branches, axisY, viewportWidth,
  )) {
    pushWorldLine(
      verts,
      mark.x,
      mark.y - SCENE_MARK_HALF_LEN,
      mark.x,
      mark.y + SCENE_MARK_HALF_LEN,
      view,
      0.97,
      0.86,
      0.42,
      0.95,
      MARK_HALF_WIDTH,
    );
  }
}

function sceneMarkYs(
  branches: readonly RailBranch[],
  axisY: number,
  start: SceneStart,
): number[] {
  if (branches.length === 0) {
    return [axisY];
  }
  const branch = branches.find((item) => (
    item.label === start.locationLabel
    && start.startMs >= item.startMs
    && start.startMs < item.endMs
  ));
  if (branch === undefined) {
    return [];
  }
  return [axisY + branchOffsetY(branch, start.startMs)];
}

export function railWorldX(ms: number, viewportWidth: number): number {
  return timelineHomeMarkerCenterX(viewportWidth) + ms / MS_PER_PIXEL;
}
